import React, { memo } from 'react';
import classNames from 'classnames';

import type { FCC } from '@rikstv/play-common/src/utils/types/typeUtils';

import type { IconComponentType, IconSize } from '../../icons/svgToIconElement';
import { Link } from '../../router/Link';

import styles from './ActionButton.module.css';

export type ButtonStyle = 'primary' | 'secondary' | 'tertiary' | 'inlineLink' | 'inlineLinkLight';

export interface CommonButtonProps extends Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  id?: string;
  buttonStyle?: ButtonStyle;
  selected?: boolean;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  isLoading?: boolean;

  icon?: IconComponentType;
  iconPosition?: 'left' | 'right';
  hasDarkBackground?: boolean;
}

type ActionButtonProps = CommonButtonProps & {
  iconOnly: boolean;
  iconSize: IconSize;
} & (
    | {
        buttonType: 'button';
        onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
      }
    | {
        buttonType: 'link';
        to: string;
        onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
      }
  );

export const InternalActionButton: FCC<ActionButtonProps> = memo(
  ({
    id,
    buttonStyle = 'primary',
    selected = false,
    className,
    ariaLabel,
    disabled,
    icon: Icon,
    iconPosition,
    iconOnly,
    isLoading,
    iconSize,
    children,
    hasDarkBackground = true,
    ...props
  }) => {
    const commonProps = {
      id,
      className: classNames(
        className,
        styles.actionButton,
        hasDarkBackground ? styles.light : styles.dark,
        styles[buttonStyle as keyof typeof styles],
        {
          [styles.selected]: selected,
          [styles.iconOnly]: iconOnly,
          [styles.iconOnlyLarge]: iconOnly && iconSize === 'large',
        }
      ),
      'aria-disabled': disabled,
      'aria-label': ariaLabel,
      'aria-busy': isLoading || undefined,
    };

    const contents =
      Icon && iconOnly ? (
        <Icon iconSize={iconSize} />
      ) : (
        <>
          {Icon && iconPosition === 'left' && <Icon spacingRight iconSize={iconSize} />}
          {children}
          {Icon && iconPosition === 'right' && <Icon spacingLeft iconSize={iconSize} />}
        </>
      );

    if (props.buttonType === 'link') {
      return (
        <Link withDefaultStyle={false} to={props.to} onClick={props.onClick} {...commonProps}>
          {contents}
        </Link>
      );
    } else {
      const clickHandler = isLoading ? undefined : props.onClick;
      const { buttonType: _, ...buttonProps } = props;
      return (
        <button onClick={clickHandler} {...commonProps} disabled={disabled || isLoading} {...buttonProps}>
          {contents}
        </button>
      );
    }
  }
);
